import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, X } from "lucide-react";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { FormDialog, ActionButton, TextInput, SelectInput, Field } from "../common";

const money = (n) => fmtMoney(Number(n) || 0);
let KEY = 0;

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDay = (iso) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
const defaultFrom = (d) => {
  const bt = d.billed_through ? String(d.billed_through).slice(0, 10) : "";
  if (bt) return addDay(bt);
  return String(d.billing_start_date || d.deliver_by_date || "").slice(0, 10) || todayISO();
};
const overlaps = (from, to, periods) => (periods || []).some((p) => from <= (p.to || "") && (p.from || "") <= to);

const buildTicket = (data, dispatchId, primary) => {
  const ss = data.stages || [];
  const anyIndiv = ss.some((s) => s.stage !== "ALL" && s.billed);
  const allB = ss.some((s) => s.stage === "ALL" && s.billed);
  const dis = (s) => s.billed || (s.stage === "ALL" && anyIndiv) || (s.stage !== "ALL" && allB);
  const cur = ss.find((s) => s.stage === data.current_stage);
  const open = ss.find((s) => !dis(s));
  const stage = cur && !dis(cur) ? cur.stage : (open ? open.stage : (data.current_stage || ""));
  const stageDays = Object.fromEntries(ss.map((s) => [s.stage, s.days]))[stage];
  const lines = data.candidate_lines.map((l) => ({
    ...l,
    include: !l.billed,
    quantity: (stageDays != null && l.kind === "rental") ? stageDays : l.quantity,
    _k: ++KEY,
  }));
  const bf = defaultFrom(data.dispatch);
  const t = todayISO();
  return {
    key: ++KEY,
    id: dispatchId,
    dispatch_number: data.dispatch.dispatch_number,
    customer_name: data.dispatch.customer_name,
    delivery_location: data.dispatch.delivery_location,
    truck_name: data.dispatch.truck_name,
    trailer_name: data.dispatch.trailer_name,
    catalog: data.catalog || [],
    stages: ss,
    lines,
    stage,
    rateToAdd: "",
    bill_from: bf,
    bill_to: bf > t ? bf : t,
    billed_periods: data.dispatch.billing_periods || [],
    primary,
  };
};

const stageLabel = (s) => (s.stage === "ALL"
  ? `All stages combined — ${s.days} days total${s.billed ? " · already billed (locked)" : ""}`
  : `Stage ${s.stage} — ${s.days} day${s.days === 1 ? "" : "s"}${s.billed ? " · already billed (locked)" : ""}`);

export const CreateProformaDialog = ({ dispatch, onClose, onCreated }) => {
  const [preview, setPreview] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoSel, setPhotoSel] = useState({});
  const [thumbs, setThumbs] = useState({});
  const [requesterId, setRequesterId] = useState("");
  const [ticketToAdd, setTicketToAdd] = useState("");
  const [addingTicket, setAddingTicket] = useState(false);
  const [mode, setMode] = useState("combined");
  const thumbsRef = useRef({});
  useEffect(() => { thumbsRef.current = thumbs; }, [thumbs]);
  useEffect(() => () => { Object.values(thumbsRef.current).forEach((u) => URL.revokeObjectURL(u)); }, []);

  useEffect(() => {
    if (!dispatch) return;
    setPreview(null); setTickets([]); setNotes(""); setTicketToAdd(""); setMode("combined");
    api.get(`/proformas/billable-preview?dispatch_id=${dispatch.id}`).then((r) => {
      setPreview(r.data);
      setTickets([buildTicket(r.data, dispatch.id, true)]);
      setRequesterId(r.data.default_requester_id || "");
      const ph = r.data.photos || [];
      setPhotoSel(Object.fromEntries(ph.map((p) => [p.id, true])));
      ph.forEach(async (p) => {
        try {
          const resp = await api.get(`/fractank/dispatches/${dispatch.id}/photos/${p.id}/download`, { responseType: "blob" });
          setThumbs((prev) => ({ ...prev, [p.id]: URL.createObjectURL(resp.data) }));
        } catch { /* skip unreadable thumbnail */ }
      });
    }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchTicket = (ti, patch) => setTickets((prev) => prev.map((t, i) => (i === ti ? { ...t, ...patch } : t)));
  const setLine = (ti, li, patch) => setTickets((prev) => prev.map((t, i) => (i === ti ? { ...t, lines: t.lines.map((l, j) => (j === li ? { ...l, ...patch } : l)) } : t)));
  const removeLine = (ti, li) => setTickets((prev) => prev.map((t, i) => (i === ti ? { ...t, lines: t.lines.filter((_, j) => j !== li) } : t)));
  const addCustom = (ti) => setTickets((prev) => prev.map((t, i) => (i === ti ? { ...t, lines: [...t.lines, { include: true, kind: "custom", description: "", uom: "", quantity: 1, rate: 0, _k: ++KEY }] } : t)));
  const addFromRate = (ti) => setTickets((prev) => prev.map((t, i) => {
    if (i !== ti) return t;
    const r = (t.catalog || []).find((x) => x.id === t.rateToAdd);
    if (!r) return t;
    return { ...t, rateToAdd: "", lines: [...t.lines, { include: true, kind: "rate", description: r.name || "Item", uom: r.uom || "", quantity: 1, rate: r.rate || 0, _k: ++KEY }] };
  }));
  const changeStage = (ti, val) => setTickets((prev) => prev.map((t, i) => {
    if (i !== ti) return t;
    const d = Object.fromEntries((t.stages || []).map((s) => [s.stage, s.days]))[val];
    return { ...t, stage: val, lines: d != null ? t.lines.map((l) => (l.kind === "rental" ? { ...l, quantity: d } : l)) : t.lines };
  }));

  const addTicket = async (did) => {
    if (!did) return;
    setAddingTicket(true);
    try {
      const r = await api.get(`/proformas/billable-preview?dispatch_id=${did}`);
      setTickets((prev) => [...prev, buildTicket(r.data, did, false)]);
      setTicketToAdd("");
      toast.success(`Added ${r.data.dispatch.dispatch_number}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setAddingTicket(false); }
  };
  const removeTicket = (ti) => setTickets((prev) => prev.filter((_, i) => i !== ti));

  const optDisabled = (t, s) => {
    const anyIndiv = (t.stages || []).some((x) => x.stage !== "ALL" && x.billed);
    const allB = (t.stages || []).some((x) => x.stage === "ALL" && x.billed);
    return s.billed || (s.stage === "ALL" && anyIndiv) || (s.stage !== "ALL" && allB);
  };
  const stageLocked = (t) => (t.stages || []).length > 0 && (t.stages || []).some((s) => s.stage === t.stage && optDisabled(t, s));
  const billableTickets = tickets.filter((t) => !stageLocked(t));
  const allSelected = billableTickets.flatMap((t) => t.lines.filter((l) => l.include).map((l) => ({ ...l, ticket: t.dispatch_number, dispatch_id: t.id })));
  const total = allSelected.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const jobCount = billableTickets.filter((t) => t.lines.some((l) => l.include)).length;
  const individual = mode === "individual" && tickets.length > 1;

  const ticketOptions = preview ? [
    { value: preview.dispatch.id, label: `${preview.dispatch.dispatch_number} — ${preview.dispatch.delivery_location || "—"} (this ticket)` },
    ...(preview.other_tickets || []).map((t) => ({ value: t.id, label: `${t.dispatch_number} — ${t.delivery_location || "—"}` })),
  ] : [];

  const submit = async () => {
    if (!allSelected.length) { toast.error("Select at least one line"); return; }
    if (allSelected.some((l) => !l.description.trim())) { toast.error("Every line needs a description"); return; }
    const badRange = billableTickets.find((t) => t.bill_from && t.bill_to && t.bill_from > t.bill_to);
    if (badRange) { toast.error(`${badRange.dispatch_number}: billing period "from" is after "to"`); return; }
    const dupes = billableTickets.filter((t) => t.bill_from && t.bill_to && overlaps(t.bill_from, t.bill_to, t.billed_periods));
    if (dupes.length && !window.confirm(`${dupes.map((t) => t.dispatch_number).join(", ")} already billed for an overlapping period. Create this Proforma anyway?`)) return;
    const periodOf = (t) => (t.bill_from && t.bill_to ? [{ dispatch_id: t.id, from: t.bill_from, to: t.bill_to }] : []);
    const photoIds = (preview.photos || []).filter((p) => photoSel[p.id]).map((p) => p.id);
    const mkLines = (t) => t.lines.filter((l) => l.include).map((l) => ({ description: l.description, uom: l.uom || null, quantity: Number(l.quantity) || 0, rate: Number(l.rate) || 0, kind: l.kind, asset_id: l.asset_id || null, ticket: t.dispatch_number, dispatch_id: t.id }));
    setBusy(true);
    try {
      if (individual) {
        const jobs = billableTickets.map((t) => ({ t, lines: mkLines(t) })).filter((j) => j.lines.length);
        const created = [];
        for (const j of jobs) {
          const r = await api.post("/proformas", {
            dispatch_id: j.t.id, dispatch_ids: [j.t.id], stage: j.t.stage || null,
            requester_id: requesterId || null, notes: notes || null,
            photo_ids: j.t.id === preview.dispatch.id ? photoIds : [],
            lines: j.lines, billing_periods: periodOf(j.t),
          });
          created.push(r.data);
        }
        toast.success(`Created ${created.length} proformas: ${created.map((c) => c.number).join(", ")}`);
        onCreated?.(created[created.length - 1]);
      } else {
        const r = await api.post("/proformas", {
          dispatch_id: billableTickets[0].id, dispatch_ids: billableTickets.map((t) => t.id), stage: billableTickets[0].stage || null,
          requester_id: requesterId || null, notes: notes || null, photo_ids: photoIds,
          lines: billableTickets.flatMap(mkLines), billing_periods: billableTickets.flatMap(periodOf),
        });
        toast.success(`Proforma ${r.data.number} created`);
        onCreated?.(r.data);
      }
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog open={!!dispatch} onOpenChange={(v) => !v && onClose()} title="Create Proforma Sales Order" testId="proforma-create-dialog" className="max-w-3xl">
      {!preview ? (
        <p className="text-sm text-slate-400 py-8 text-center" data-testid="proforma-loading">Loading billable items…</p>
      ) : (
        <div className="space-y-4">
          <Field label="Requester (PDF email recipient)">
            <SelectInput testId="proforma-requester-select" value={requesterId} onChange={(e) => setRequesterId(e.target.value)} placeholder="Select requester…"
              options={[{ value: "", label: "No requester selected" }, ...(preview.requesters || []).map((r) => ({ value: r.id, label: r.email ? `${r.name} — ${r.email}` : `${r.name} (no email)` }))]} />
          </Field>

          {tickets.map((t, ti) => (
            <div key={t.key} className="border border-slate-800 rounded-lg p-3 space-y-3 bg-slate-900/30" data-testid={`proforma-ticket-section-${ti}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-400" data-testid={`proforma-ticket-summary-${ti}`}>
                  <span className="font-mono text-amber-400">{t.dispatch_number}</span> · {t.customer_name} · {t.delivery_location || "—"}
                  {t.truck_name && <> · Truck: {t.truck_name}</>}{t.trailer_name && <> · Trailer: {t.trailer_name}</>}
                </p>
                {!t.primary && <button type="button" data-testid={`proforma-remove-ticket-${ti}`} onClick={() => removeTicket(ti)} className="shrink-0 text-slate-500 hover:text-red-400 flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider"><X size={13} /> Remove</button>}
              </div>

              {t.stages?.length > 0 && (
                <Field label="Well Stage to bill (sets rental days)">
                  <SelectInput testId={`proforma-stage-select-${ti}`} value={t.stage} onChange={(e) => changeStage(ti, e.target.value)}
                    options={t.stages.map((s) => ({ value: s.stage, label: stageLabel(s), disabled: optDisabled(t, s) }))} />
                </Field>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Billing period from">
                  <TextInput testId={`proforma-bill-from-${ti}`} type="date" value={t.bill_from || ""} onChange={(e) => patchTicket(ti, { bill_from: e.target.value })} />
                </Field>
                <Field label="Billing period to">
                  <TextInput testId={`proforma-bill-to-${ti}`} type="date" value={t.bill_to || ""} onChange={(e) => patchTicket(ti, { bill_to: e.target.value })} />
                </Field>
              </div>
              {(t.billed_periods || []).length > 0 && (
                <p className="text-[10px] font-mono text-slate-500" data-testid={`proforma-billed-history-${ti}`}>
                  Already billed: {t.billed_periods.map((p) => `${p.from} → ${p.to}`).join(" · ")}
                </p>
              )}

              {stageLocked(t) && (
                <p className="text-[11px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1.5" data-testid={`proforma-stage-locked-${ti}`}>
                  This stage has already been added to a Proforma. Delete that Proforma in Accounting Logic to bill it again.
                </p>
              )}

              <div className={`overflow-x-auto border border-slate-800 rounded-md ${stageLocked(t) ? "opacity-40 pointer-events-none" : ""}`}>
                <table className="w-full text-sm" data-testid={`proforma-lines-table-${ti}`}>
                  <thead><tr className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <th className="p-2 w-8"></th><th className="p-2 text-left">Product / Service</th><th className="p-2 w-24">UOM</th><th className="p-2 w-20">Qty</th><th className="p-2 w-28">Rate</th><th className="p-2 w-28 text-right">Total</th><th className="p-2 w-8"></th>
                  </tr></thead>
                  <tbody>
                    {t.lines.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-500 text-xs">No billable items — add a line below.</td></tr>}
                    {t.lines.map((l, li) => (
                      <tr key={l._k} className={`border-b border-slate-800/60 ${l.billed ? "opacity-50" : ""}`} data-testid={`proforma-line-${ti}-${li}`}>
                        <td className="p-2 text-center"><input type="checkbox" data-testid={`proforma-line-include-${ti}-${li}`} checked={l.include} disabled={l.billed} onChange={(e) => setLine(ti, li, { include: e.target.checked })} /></td>
                        <td className="p-2"><TextInput testId={`proforma-line-desc-${ti}-${li}`} value={l.description} onChange={(e) => setLine(ti, li, { description: e.target.value })} />{l.billed && <span data-testid={`proforma-line-billed-${ti}-${li}`} className="mt-1 inline-block text-[9px] font-mono uppercase tracking-wider text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">Already proformaed</span>}</td>
                        <td className="p-2"><TextInput testId={`proforma-line-uom-${ti}-${li}`} value={l.uom || ""} onChange={(e) => setLine(ti, li, { uom: e.target.value })} /></td>
                        <td className="p-2"><TextInput testId={`proforma-line-qty-${ti}-${li}`} type="number" value={l.quantity} onChange={(e) => setLine(ti, li, { quantity: e.target.value })} /></td>
                        <td className="p-2"><TextInput testId={`proforma-line-rate-${ti}-${li}`} type="number" value={l.rate} onChange={(e) => setLine(ti, li, { rate: e.target.value })} /></td>
                        <td className="p-2 text-right font-mono text-emerald-400">{money((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</td>
                        <td className="p-2 text-center"><button type="button" data-testid={`proforma-line-remove-${ti}-${li}`} onClick={() => removeLine(ti, li)} className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={`flex flex-wrap items-end gap-2 ${stageLocked(t) ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex-1 min-w-[200px]">
                  <Field label="Add from Product / Service Items">
                    <SelectInput testId={`proforma-rate-picker-${ti}`} value={t.rateToAdd} onChange={(e) => patchTicket(ti, { rateToAdd: e.target.value })}
                      options={[{ value: "", label: "Select an item…" }, ...(t.catalog || []).map((r) => ({ value: r.id, label: `${r.name || "Item"} — ${money(r.rate)}/${r.uom || "ea"}` }))]} />
                  </Field>
                </div>
                <ActionButton testId={`proforma-add-rate-btn-${ti}`} type="button" variant="secondary" onClick={() => addFromRate(ti)} disabled={!t.rateToAdd}>Add Rate</ActionButton>
                <ActionButton testId={`proforma-add-custom-btn-${ti}`} type="button" variant="secondary" onClick={() => addCustom(ti)}><Plus size={14} /> Custom Line</ActionButton>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-end gap-2" data-testid="proforma-add-ticket-row">
            <div className="flex-1 min-w-[220px]">
              <Field label="Add Another Ticket / Stage (same customer)">
                <SelectInput testId="proforma-ticket-picker" value={ticketToAdd} onChange={(e) => setTicketToAdd(e.target.value)}
                  options={[{ value: "", label: "Select a ticket to combine…" }, ...ticketOptions]} />
              </Field>
            </div>
            <ActionButton testId="proforma-add-ticket-btn" type="button" variant="secondary" onClick={() => addTicket(ticketToAdd)} disabled={!ticketToAdd || addingTicket}><Plus size={14} /> {addingTicket ? "Adding…" : "Add Another Ticket"}</ActionButton>
          </div>

          {tickets.length > 1 && (
            <div className="border border-slate-800 rounded-lg p-3" data-testid="proforma-mode-row">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">How should these {tickets.length} tickets / stages be billed?</p>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer" data-testid="proforma-mode-combined">
                  <input type="radio" name="proforma-mode" checked={mode === "combined"} onChange={() => setMode("combined")} className="mt-1" />
                  <span><span className="font-medium text-slate-100">One combined Proforma</span><br /><span className="text-xs text-slate-500">All selected tickets / stages on a single Proforma, grouped by ticket.</span></span>
                </label>
                <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer" data-testid="proforma-mode-individual">
                  <input type="radio" name="proforma-mode" checked={mode === "individual"} onChange={() => setMode("individual")} className="mt-1" />
                  <span><span className="font-medium text-slate-100">Separate Proforma for each</span><br /><span className="text-xs text-slate-500">Creates one Proforma per section below ({jobCount} with selected lines).</span></span>
                </label>
              </div>
            </div>
          )}

          {(preview.photos || []).length > 0 && (
            <div data-testid="proforma-photos">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Photos on Proforma — choose which to include</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {preview.photos.map((p) => (
                  <button type="button" key={p.id} data-testid={`proforma-photo-${p.id}`} onClick={() => setPhotoSel((s) => ({ ...s, [p.id]: !s[p.id] }))}
                    className={`relative rounded border-2 overflow-hidden transition-opacity ${photoSel[p.id] ? "border-amber-500" : "border-slate-700 opacity-40"}`}>
                    {thumbs[p.id] ? <img src={thumbs[p.id]} alt={p.caption || "photo"} className="w-full h-20 object-cover" /> : <div className="w-full h-20 bg-slate-800 flex items-center justify-center text-[10px] text-slate-500">Loading…</div>}
                    <span className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${photoSel[p.id] ? "bg-amber-500 text-slate-900" : "bg-slate-900/80 text-slate-400"}`}>{photoSel[p.id] ? "✓" : ""}</span>
                    {p.is_damage && <span className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] px-1 font-bold">DAMAGE</span>}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1" data-testid="proforma-photos-count">{(preview.photos || []).filter((p) => photoSel[p.id]).length} of {preview.photos.length} selected · each ≤ 2" × 3" on the PDF</p>
            </div>
          )}

          <Field label="Notes (optional)">
            <TextInput testId="proforma-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Moved tanks off location" />
          </Field>

          <div className="flex items-center justify-between pt-2 border-t border-slate-800">
            <span className="font-heading text-xl font-bold text-amber-400" data-testid="proforma-total">{money(total)}</span>
            <ActionButton testId="proforma-submit-btn" onClick={submit} disabled={busy}><FileText size={16} /> {busy ? "Creating…" : (individual ? `Create ${jobCount} Proformas` : "Create Proforma")}</ActionButton>
          </div>
        </div>
      )}
    </FormDialog>
  );
};
